import { isChunkLoadError } from './chunkLoadError';
import { markAppStale, getAppVersionState } from './appVersionStore';

type ChunkLoader = () => Promise<unknown>;

/** 預載之間的間隔，避免一次把頻寬吃光、擋到使用者自己的操作 */
export const PREFETCH_GAP_MS = 60;
/** 瀏覽器沒有 requestIdleCallback 時，等畫面穩定後再開始 */
export const PREFETCH_FALLBACK_DELAY_MS = 2000;
const IDLE_CALLBACK_TIMEOUT_MS = 10_000;
const SLOW_CONNECTION_TYPES = new Set(['slow-2g', '2g']);

interface NetworkInformationLike {
    saveData?: boolean;
    effectiveType?: string;
}

const registeredLoaders: ChunkLoader[] = [];
let hasPrefetchStarted = false;

/** lazyWithRetry 建立時呼叫；清單順序即預載順序 */
export function registerPrefetchLoader(loader: ChunkLoader): void {
    registeredLoaders.push(loader);
}

export function getRegisteredPrefetchLoaders(): readonly ChunkLoader[] {
    return registeredLoaders;
}

export function shouldSkipPrefetch(connection?: NetworkInformationLike): boolean {
    if (!connection) return false;
    return (
        connection.saveData === true || SLOW_CONNECTION_TYPES.has(connection.effectiveType ?? '')
    );
}

interface SequentialPrefetchOptions {
    gapMs?: number;
    wait?: (delayMs: number) => Promise<void>;
    isCancelled?: () => boolean;
}

function waitForDelay(delayMs: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, delayMs));
}

/**
 * 一個接一個載入。失敗一律吞掉（預載不該影響使用者），但 chunk 取不到代表已經換版，
 * 此時標記過期並停止，繼續載入只會得到更多 404。
 */
export async function prefetchSequentially(
    loaders: readonly ChunkLoader[],
    options: SequentialPrefetchOptions = {}
): Promise<void> {
    const { gapMs = PREFETCH_GAP_MS, wait = waitForDelay, isCancelled = () => false } = options;

    for (const loader of loaders) {
        if (isCancelled() || getAppVersionState().isStale) return;
        try {
            await loader();
        } catch (error) {
            if (isChunkLoadError(error)) {
                markAppStale();
                return;
            }
            console.debug('[prefetch] chunk skipped:', error);
        }
        await wait(gapMs);
    }
}

/** 讓端對端測試知道預載已跑完，不必靠固定等待時間 */
export const PREFETCH_DONE_ATTRIBUTE = 'data-chunk-prefetch';

function markPrefetchFinished(): void {
    if (!getAppVersionState().isStale) {
        document.documentElement.setAttribute(PREFETCH_DONE_ATTRIBUTE, 'done');
    }
}

type IdleScheduler = (task: () => void) => () => void;

function scheduleWhenIdle(task: () => void): () => void {
    if (typeof window.requestIdleCallback === 'function') {
        const handle = window.requestIdleCallback(task, { timeout: IDLE_CALLBACK_TIMEOUT_MS });
        return () => window.cancelIdleCallback(handle);
    }
    const handle = window.setTimeout(task, PREFETCH_FALLBACK_DELAY_MS);
    return () => window.clearTimeout(handle);
}

interface IdlePrefetchOptions {
    extraLoaders?: readonly ChunkLoader[];
    isDevelopment?: boolean;
    connection?: NetworkInformationLike;
    schedule?: IdleScheduler;
    gapMs?: number;
    wait?: (delayMs: number) => Promise<void>;
}

/**
 * 登入後的外殼畫面出現、瀏覽器閒下來時，把所有路由頁面與翻譯檔先抓進快取。
 * 已經開著的分頁因此不再依賴伺服器上的舊檔，部署換版後仍能正常換頁。
 * 整個頁面生命週期只跑一次；回傳的函式可取消尚未開始的排程。
 */
export function scheduleIdlePrefetch(options: IdlePrefetchOptions = {}): () => void {
    const {
        extraLoaders = [],
        isDevelopment = import.meta.env.DEV,
        connection = (navigator as Navigator & { connection?: NetworkInformationLike }).connection,
        schedule = scheduleWhenIdle,
        ...sequentialOptions
    } = options;

    if (isDevelopment || hasPrefetchStarted || shouldSkipPrefetch(connection)) {
        return () => undefined;
    }

    let isCancelled = false;
    const cancelSchedule = schedule(() => {
        if (isCancelled || hasPrefetchStarted) return;
        hasPrefetchStarted = true;
        void prefetchSequentially([...registeredLoaders, ...extraLoaders], sequentialOptions).then(
            markPrefetchFinished
        );
    });

    return () => {
        isCancelled = true;
        cancelSchedule();
    };
}

/** 只給測試用 */
export function resetPrefetchStateForTest(): void {
    registeredLoaders.length = 0;
    hasPrefetchStarted = false;
}
